import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import {
  getActiveEntryInLeague,
  getH2HLeague,
  getH2HMatches,
  getH2HNewEntries,
  getH2HStandings,
  getLeagueEntries,
  getLeagueEntriesError,
} from "core-integration/src/store/leagues/reducers";
import {
  fetchH2HLeagueMatches,
  fetchH2HLeagueStandings,
  fetchLeagueEntries,
} from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import Alert from "plos/src/components/alerts/Alert";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { rightSidebarLayout } from "plos/src/layouts";
import qs from "qs";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { HelmetHead } from "../../../HelmetHead";
import ReportNameButton from "../../../ReportNameButton";
import { MatchesFilters, MatchesList, MatchItemSkeleton } from "../../Matches/";
import { EntryFilter } from "../../Matches/MatchesFilters/EntryFilter";
import { EventFilter } from "../../Matches/MatchesFilters/EventFilter";
import { reportButtonWrapper, StandingsHeading } from "../../shared";
import { H2HNav } from "../H2HNav";
import { matchesH2HMainContent } from "./matchesH2H.css";

const MatchesH2H = () => {
  const { t } = useTranslation("leaguesAndCups");
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();
  const { search } = useLocation();
  const { leagueId } = useParams();

  const leagueNumber = leagueId ? parseInt(leagueId, 10) : 0;
  const query = useMemo(
    () =>
      qs.parse(search, {
        ignoreQueryPrefix: true,
      }),
    [search],
  );
  const entryNumber = parseInt(query.entry as string, 10) || 0;
  const defaultEntryNumber = parseInt(query.default_entry as string, 10) || 0;

  const now = useSelector(getCurrentEvent);
  const eventNumber = query.event
    ? parseInt(query.event as string, 10)
    : now?.id || 0;
  const pageMatches = parseInt(query.page_matches as string, 10) || 1;

  const isPlayerInLeague = useSelector((state: RootState) =>
    Boolean(getActiveEntryInLeague(state, leagueNumber, "h2h")),
  );
  const league = useSelector((state: RootState) =>
    getH2HLeague(state, leagueNumber),
  );
  const leagueEntries = useSelector((state: RootState) =>
    getLeagueEntries(state, leagueNumber),
  );
  const leagueEntriesError = useSelector((state: RootState) =>
    getLeagueEntriesError(state, leagueNumber),
  );
  const matches = useSelector((state: RootState) =>
    leagueNumber
      ? getH2HMatches(
          state,
          leagueNumber,
          entryNumber,
          eventNumber,
          pageMatches,
        )
      : null,
  );

  const newEntries = useSelector((state: RootState) =>
    getH2HNewEntries(state, leagueNumber, 1),
  );
  const player = useSelector(getPlayerData);

  const standings = useSelector((state: RootState) =>
    getH2HStandings(state, leagueNumber, 1),
  );

  const isLargeLeague = useMemo(() => {
    return (
      leagueEntriesError?.badRequest?.non_field_errors[0]?.code ===
      "large_leagues_forbidden"
    );
  }, [leagueEntriesError]);

  const buildMatchesUrl = useCallback(
    (
      leagueNum: number,
      eventNum: number,
      entryNum: number,
      pageNum: number,
    ) => {
      let url = `/leagues/${leagueNum}/matches/h?page_matches=${pageNum}`;
      if (entryNum) url += `&entry=${entryNum}`;
      if (now?.id && eventNum !== now.id) url += `&event=${eventNum}`;
      return url;
    },
    [now],
  );

  useEffect(() => {
    if (leagueNumber) {
      // Have to fetch standings to get league
      dispatch(fetchH2HLeagueStandings(leagueNumber, 1, 1));
      // We could look at delaying these calls as they are pointless for
      // unstarted h2h leagues at the cost of some complexity as we may have
      // to wait for league to be available. As a 'good enough' we don't do
      // them before the season starts ...
      if (now) {
        dispatch(fetchLeagueEntries(leagueNumber));
        dispatch(
          fetchH2HLeagueMatches(
            leagueNumber,
            entryNumber,
            eventNumber,
            pageMatches,
          ),
        );
      }
    }
  }, [leagueNumber, now, entryNumber, eventNumber, pageMatches, dispatch]);

  useEffect(() => {
    if (isLargeLeague) {
      // As isPlayerInLeague we must have a player and an entry
      const showEntry =
        defaultEntryNumber || (isPlayerInLeague ? player!.entry! : 0);
      navigate(buildMatchesUrl(leagueNumber, 0, showEntry, pageMatches), {
        replace: true,
      });
    }
  }, [
    isLargeLeague,
    leagueEntriesError,
    defaultEntryNumber,
    isPlayerInLeague,
    player,
    leagueNumber,
    pageMatches,
    buildMatchesUrl,
    navigate,
  ]);

  const DEFAULT_FILTERS = {
    event: now?.id || 0,
    entry: 0,
  };

  const handleEntryFilterChange = (entryId: number) =>
    navigate(buildMatchesUrl(leagueNumber, eventNumber, entryId, 1));

  const handleEventFilterChange = (eventId: number) => {
    navigate(buildMatchesUrl(leagueNumber, eventId, entryNumber, 1));
  };

  const handleResetFilters = () => {
    navigate(
      buildMatchesUrl(
        leagueNumber,
        DEFAULT_FILTERS.event,
        DEFAULT_FILTERS.entry,
        1,
      ),
    );
  };

  if (!league || !newEntries) {
    return null;
  }

  // If we have new entries then switch to dedicated page
  if (!now || newEntries.results.length) {
    navigate(`/leagues/${league.id}/new-entries/h`, { replace: true });
    return;
  }

  const areMatchesBeingCreated =
    now.id === league.start_event && standings && !standings.results.length;

  return (
    <>
      <HelmetHead
        title={t(
          "h2hMatches.metadata.title",
          "{{leagueName}} - Head-to-Head League Matches | Fantasy Premier League",
          {
            leagueName: league.name,
          },
        )}
        description={t(
          "h2hMatches.metadata.description",
          "To view the {{leagueName}} invitational head-to-head league matches, as well as creating & joining new leagues, visit the official website of the Premier League.",
          {
            leagueName: league.name,
          },
        )}
      />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <StandingsHeading
            type="h2h"
            league={league}
            leagueNumber={leagueNumber}
          />
          <div className={matchesH2HMainContent}>
            {areMatchesBeingCreated ? (
              <p>
                {t(
                  "h2hMatches.matchesBeingGenerated",
                  "Matches are currently being generated, please check back later.",
                )}
              </p>
            ) : (
              <>
                <H2HNav view="matches" defaultEntry={defaultEntryNumber} />
                {leagueEntries && league.start_event && (
                  <MatchesFilters
                    handleResetFilters={handleResetFilters}
                    defaultFilters={DEFAULT_FILTERS}
                    entryNumber={entryNumber}
                    eventNumber={eventNumber}
                    isDisabled={!matches}
                  >
                    <>
                      <EntryFilter
                        entries={leagueEntries}
                        selectedEntryId={entryNumber}
                        handleFilterChange={handleEntryFilterChange}
                      />
                      <EventFilter
                        startEvent={league.start_event}
                        selectedEventId={eventNumber}
                        handleFilterChange={handleEventFilterChange}
                      />
                    </>
                  </MatchesFilters>
                )}
                {isLargeLeague && (
                  <Alert isContentCentered>
                    {t(
                      "h2hMatches.largeLeagueAlert",
                      "In head-to-head leagues with more than 512 teams only matches for a single team can be viewed.",
                    )}
                  </Alert>
                )}
                {!matches ? (
                  Array.from({ length: 5 }, (_, i) => (
                    <MatchItemSkeleton key={i} />
                  ))
                ) : (
                  <>
                    <MatchesList
                      selectedEntry={entryNumber}
                      isAllMine={
                        entryNumber === player?.entry && eventNumber === 0
                      }
                      matches={matches}
                      pageMatches={pageMatches}
                      toPrevious={buildMatchesUrl(
                        leagueNumber,
                        eventNumber,
                        entryNumber,
                        pageMatches - 1,
                      )}
                      toNext={buildMatchesUrl(
                        leagueNumber,
                        eventNumber,
                        entryNumber,
                        pageMatches + 1,
                      )}
                    />
                    <div className={reportButtonWrapper.mobile}>
                      <ReportNameButton league={league} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default MatchesH2H;
